import React from "react";
import { useSetRecoilState } from "recoil";
import tableState from "../../SimpleForm/Atoms/Table/state";

import DOMPurify from "dompurify";
import FieldDisabled from "../FieldDisabled";
import "./styles/RadioInput.scss";
const RadioInput = ({
  input,
  inputOnChange: inputOnChangeProp,
  meta,
  schemaProps: {
    machine_name,
    choice,
    value,
    checked,
    disabled,
    disableWhenField,
    conditionalClearSchema,
    clearSchema,
    clearTableErrors,
    conditionalClearTableErrors,
    clearTableRows,
    clearTableFields,
    conditionalSetTableErrors,
    clearFiles,
    setSchema,
    setPagesErrors,
    removePagesErrors,
    parent_machine_name,
    inputOnChange: inputOnChangeSchema,
  },
  formApi,
  addPageVisited,
  addPageErrored,
  removePageVisited,
  removePageErrored,
  disabledViaReuse,
  ...rest
}) => {
  const setTableErrors = useSetRecoilState(tableState);
  // inputOnChangeProp: passed directly by callers that render RadioInput manually (e.g. WageCalculator)
  // inputOnChangeSchema: defined in the field schema and passed via schemaProps by Page.js —
  //   Page.js spreads the entire field object into schemaProps but does NOT extract inputOnChange
  //   as a standalone prop, so schema-defined callbacks only arrive through schemaProps.
  const inputOnChange = inputOnChangeProp || inputOnChangeSchema;
  let clearFields = () => {
    clearSchema.forEach((c) => {
      let fieldToClear = parent_machine_name
        ? `${parent_machine_name}[0]${c}`
        : c;
      formApi.change(fieldToClear, null);
    });
  };
  let clearTableFieldErrors = () => {
    clearTableErrors.forEach((c) => {
      // clearing out table errors
      setTableErrors((errors) => {
        let newTableErrors = JSON.parse(JSON.stringify(errors));

        if (Object.keys(errors).length) {
          Object.keys(errors).forEach((key) => {
            if (key === c) {
              newTableErrors[`${key}`] = {};
            }
          });
        }

        return newTableErrors;
      });
    });
  };

  const conditionallyClearFields = () => {
    conditionalClearSchema.forEach((schema) => {
      const { clear, when, is } = schema;
      if (formApi.getState().values[when] === is) {
        formApi.change(clear, null);
      }
    });
  };

  const clearRowsFromTable = () => {
    clearTableRows.forEach((schema) => {
      const { table, field, find } = schema;
      const tableValues = formApi.getState().values[table];

      if (!!tableValues) {
        const index = tableValues.findIndex((row) => row[field] === find);
        if (index > -1) {
          tableValues.splice(index, 1);
          formApi.change(table, tableValues);
        }
      }
    });
  };

  const clearFieldsFromTable = () => {
    clearTableFields?.forEach((schema) => {
      const { table, field, to } = schema;
      const tableValues = formApi?.getState()?.values?.[table];

      if (!!tableValues) {
        tableValues?.forEach((row) => {
          row[field] = to ?? null;
        });
        formApi?.change(table, tableValues);
      }
    });
  };

  const conditionallySetTableErrors = () => {
    const { table, extraData, setPageErrors, checkTableFields } =
      conditionalSetTableErrors;
    const formValues = formApi?.getState()?.values;

    let conditionalCheck = true;
    if (extraData) {
      const { when, is, operator } = extraData;
      switch (operator) {
        case "===":
          conditionalCheck = formValues?.[when] === is;
          break;
        default:
          conditionalCheck = false;
      }
    }
    if (conditionalCheck) {
      const tableValue = formValues?.[table];
      let hasPageError = false;
      if (tableValue) {
        tableValue?.forEach((row) => {
          checkTableFields?.forEach((field) => {
            if (row?.[field?.if] === field?.is || !row?.[field?.if]) {
              hasPageError = true;
              row.error = true;
            }
          });
        });
      }

      if (hasPageError) {
        setPageErrors &&
          setPageErrors?.forEach((page) => {
            addPageVisited(page);
            addPageErrored(page);
          });
      }
    }
  };

  const conditionallyClearTableErrors = () => {
    const { table, extraData, clearPageErrors } = conditionalClearTableErrors;
    const formValues = formApi?.getState()?.values;

    let clearError = true;

    if (extraData) {
      const { when, is, operator } = extraData;
      switch (operator) {
        case "&&":
          if (Array.isArray(when) && Array.isArray(is)) {
            when?.forEach((field, index) => {
              let currentWhen = formValues?.[field];
              let currentIs = is?.[index];

              // Default "is" field level operation
              let fieldOperation = currentWhen === currentIs;

              // Logical "is not" field level operation
              if (currentIs[0] === "!") {
                fieldOperation = currentWhen !== currentIs?.substring(1);
              }

              if (!fieldOperation) {
                clearError = false;
              }
            });
          }
          break;
        default:
          clearError = true;
      }
    }

    if (clearError) {
      const tableValue = formValues?.[table];

      if (!!tableValue?.length) {
        tableValue?.forEach((row) => {
          row.error = false;
        });
      }
      clearPageErrors &&
        clearPageErrors?.forEach((page) => {
          removePageVisited(page);
          removePageErrored(page);
        });
    }
  };

  let deleteFiles = () => {
    const values = formApi.getState().values;
    const hasFiles =
      values &&
      values["files"] &&
      Array.isArray(values["files"]) &&
      values["files"].length > 0;
    if (hasFiles) {
      const updatedFiles = values["files"].filter(
        (file) => !clearFiles.includes(file.machine_name)
      );
      formApi.change("files", updatedFiles);
    }
  };
  let setFields = () => {
    setSchema.forEach((c) => {
      // This more robust logic is currently only being used in 1 field: 9141 E.2
      if (c?.if?.length > 0) {
        if (
          c?.equals?.length > 0 &&
          formApi.getState().values[c.if] === c.equals
        ) {
          formApi.change(c.set, c.to);
        } else if (
          c?.notEquals?.length > 0 &&
          formApi.getState().values[c.if] !== c.notEquals
        ) {
          formApi.change(c.set, c.to);
        }
      } else {
        formApi.change(c.set, c.to);
      }
    });
  };
  let setAllPagesErrors = () => {
    const values = formApi.getState().values;
    setPagesErrors.forEach((c) => {
      if (c.is.includes(values[c.if])) {
        addPageVisited(c.to);
        addPageErrored(c.to);
      }
    });
  };
  let removeAllPagesErrors = () => {
    removePagesErrors.forEach((c) => {
      removePageVisited(c.remove);
      removePageErrored(c.remove);
    });
  };

  let isDisabled = false;
  if (disabled && disableWhenField !== undefined) {
    if (typeof disableWhenField === "string") {
      isDisabled = FieldDisabled(disabled, disableWhenField, formApi);
    } else if (Array.isArray(disableWhenField)) {
      isDisabled = FieldDisabled(
        disabled,
        disableWhenField,
        formApi,
        undefined,
        disableWhenField
      );
    } else if (typeof disableWhenField === "function") {
      isDisabled = disableWhenField(formApi.getState().values);
    } else {
      isDisabled = FieldDisabled(
        disabled,
        disableWhenField.when,
        formApi,
        undefined,
        disableWhenField.is
      );
    }
  } else if (!!disabledViaReuse) {
    isDisabled = disabledViaReuse;
  } else {
    isDisabled = disabled;
  }

  const radioEvent = (e) => {
    clearFiles && deleteFiles();
    clearSchema && clearFields();
    conditionalClearSchema && conditionallyClearFields();
    clearTableRows && clearRowsFromTable();
    clearTableFields && clearFieldsFromTable();
    clearTableErrors && clearTableFieldErrors();
    conditionalClearTableErrors && conditionallyClearTableErrors();
    setSchema && setFields();
    setPagesErrors && setAllPagesErrors();
    removePagesErrors && removeAllPagesErrors();
    conditionalSetTableErrors && conditionallySetTableErrors();
    input.onChange(e);
    inputOnChange && inputOnChange(e, formApi);
  };

  return (
    <div className="usa-radio">
      <input
        type="radio"
        className="usa-radio__input"
        {...input}
        value={value || choice}
        id={`${input.name}_${choice}`}
        checked={checked ? checked === true : input.checked}
        disabled={isDisabled}
        onChange={(e) => radioEvent(e)}
      />
      <label
        onClick={
          !isDisabled
            ? (e) => radioEvent(input.value)
            : (e) => e.preventDefault()
        }
        className="usa-radio__label"
        htmlFor={`${input.name}_${choice}`}
        aria-disabled={isDisabled}
      >
        <span
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(choice) }}
        />
      </label>
    </div>
  );
};

export default RadioInput;
